import React, { useEffect } from "react";

import { object, string, number, date, InferType } from 'yup';
import snakeToPascal, { snakeToFirstUpper } from "../../../tools";
import { initList } from "./Initializer";
import ExcelValue from "./Types";
import apiService from "../../../services/ApiService";
import dayjs from "dayjs";
import { DATESYSTEM_TO_FORMAT } from "../../../pages/subpages/Settings";
import { FieldDescriptionPopover } from "../../ModelDataPanel";
import { TailwindSelect, TailwindInput, TailwindButton, TailwindDatePicker } from "../../Tailwind";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import FieldError from "../FieldError";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useAppStore } from "../../../shared/stores/useAppStore";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../../shared/hooks/queries/useSectionLoader";
import { formatDateForApi } from "../../../utils/dateFormat";

export default function ListField({ name, field, siblingFields }) {
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const queryClient = useQueryClient();

    const invalidateQueries = () => {
        // Only invalidate section-data cache so stale sections are refetched on next
        // open.  Do NOT invalidate SCENARIO_TREE_KEY: its queryFn returns an empty
        // initial state, so an invalidation-triggered refetch would wipe the
        // lazily-loaded tree and cause model-data items to disappear.
        queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
    };

    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave, fail: failSave } = useSaveIndicator();

    // The edit typed in a row and not saved yet, keyed by that row. Saved on blur of the
    // same row, or on unmount (e.g. selecting another model_data).
    const pendingListSaveRef = React.useRef<{ elemId: number; apiElem: any } | null>(null);
    // Typing mutates the row in place; re-render so the controlled input shows each keystroke.
    const [, rerender] = React.useReducer((n: number) => n + 1, 0);

    // "Saved" only when the API accepted the write. A rejected one drops the indicator and
    // raises the error snackbar (no response means ApiService already reported it).
    const rejectSave = (response: Response | null) => {
        failSave();
        if (response) {
            useAppStore.getState().openErrorSnackBar();
        }
    }

    const saveListElem = (elemId: number, apiElem: any, onSaved?: () => void) => {
        triggerSave();
        apiService.putListElem(elemId, apiElem).then((response) => {
            if (response?.ok) {
                onSaved?.();
                resolveSave();
                invalidateQueries();
            } else {
                rejectSave(response);
            }
        })
    }

    // Save unsaved changes when the component unmounts (e.g. selecting another model_data)
    React.useEffect(() => {
        return () => {
            if (pendingListSaveRef.current) {
                apiService.putListElem(pendingListSaveRef.current.elemId, pendingListSaveRef.current.apiElem);
            }
        };
    }, []);

    let currentlySelectingAll = false;

    const isElemComplete = (elem) => {
        return Object.values(elem).filter((e: ExcelValue) => (e.value === null || e.value === 'null')).length === 0
    }

    const isListElemComplete = () => {
        if (!field.currentValue?.value) return true;
        return field.currentValue.value.filter(e => !isElemComplete(e)).length === 0
    }

    const addNewElem = () => {
        const newElem = {};
        const apiElem = {};

        Object.keys(field.elems).map((e: any) => {
            newElem[e] = { value : field.elems[e].default, address: null };
            apiElem[e] = null;
        });

        triggerSave();
        apiService.postListElem(field.id, apiElem).then(async(response) => {
            if (response?.ok) {
                const resp = await response.json()

                newElem['id'] = resp.id;

                if (field.currentValue) {
                    field.currentValue.value.push(newElem);
                }

                resolveSave();
                invalidateQueries();
                // await REFRESH_CA_SHEET();
                // await REFRESH_INPUT_SHEET();
            } else {
                rejectSave(response);
            }
        })
    }

    const floatSchema = object({
        value: number().transform((_, value) => {
            return +value.replace(/,/, '.');
          })
    })

    const updateListElemWithValidation = (elem, key, type, value) => {
        if (type == 'float') {
            if (floatSchema.isValidSync({value: value}) || !value.length) {
                elem[key].error = null;
                updateListElem(elem, key, value);
            }
            else {
                elem[key].error = "Should be a Float."
                invalidateQueries();
            }
        } else {
            updateListElem(elem, key, value);
        }
    }

    const updateListEnumElem = (elem, key, value) => {
        const apiElem = {};

        Object.keys(elem).map((e: any) => {
            if (field.elems[e] && field.elems[e].type == 'float') {
                apiElem[e] = elem[e].value ? elem[e].value.toString().replace(',', '.') : null;
            } else if (field.elems[e] && field.elems[e].type == 'datetime') {
                apiElem[e] = formatDateForApi(elem[e].value);
            } else {
                apiElem[e] = (elem[e].value != 'null') ? elem[e].value : null;
            }
        });

        apiElem[key] = (value !== undefined) ? value : null;
        if (field.elems[key].type == 'float' && apiElem[key]) {
            apiElem[key] = apiElem[key].replace(',', '.')
        }
        elem[key].value = value;

        saveListElem(elem.id, apiElem);
    }

    const updateListElem = (elem, key, value) => {
        const apiElem = {};

        Object.keys(elem).map((e: any) => {
            if (field.elems[e] && field.elems[e].type == 'float') {
                apiElem[e] = elem[e].value ? elem[e].value.toString().replace(',', '.') : null;
            } else if (field.elems[e] && field.elems[e].type == 'datetime') {
                apiElem[e] = formatDateForApi(elem[e].value);
            } else {
                apiElem[e] = (elem[e].value != 'null') ? elem[e].value : null;
            }
        });

        apiElem[key] = (value !== undefined) ? value : null;
        if (field.elems[key].type == 'float' && apiElem[key]) {
            apiElem[key] = apiElem[key].replace(',', '.')
        }
        elem[key].value = value;
        pendingListSaveRef.current = { elemId: elem.id, apiElem };
        rerender();
    }

    // On blur: save only what was typed in this row. Leaving a cell untouched sends
    // nothing, and another row's pending edit is never written to this one.
    const validateListElem = (elem) => {
        const pending = pendingListSaveRef.current;
        if (!pending || pending.elemId !== elem.id) return;
        pendingListSaveRef.current = null;
        saveListElem(pending.elemId, pending.apiElem);
    }

    const updateRelationalListElem = (elem, key, value, _displayValue, _unique) => {
        const apiElem = {};

        Object.keys(elem).map((e: any) => {
            if (field.elems[e] && field.elems[e].type == 'float') {
                apiElem[e] = elem[e].value ? elem[e].value.toString().replace(',', '.') : null;
            } else if (field.elems[e] && field.elems[e].type == 'datetime') {
                apiElem[e] = formatDateForApi(elem[e].value);
            } else {
                apiElem[e] = (elem[e].value != 'null') ? elem[e].value : null;
            }
        });

        apiElem[key] = (value !== undefined) ? value : null;

        saveListElem(elem.id, apiElem, () => {
            elem[key].value = (value !== undefined) ? value : 'null';
        });
    }

    const updateListDateElem = (elem, key, value) => {
        const apiElem = {};

        Object.keys(elem).map((e: any) => {
            if (field.elems[e] && field.elems[e].type == 'float') {
                apiElem[e] = elem[e].value ? elem[e].value.toString().replace(',', '.') : null;
            } else if (field.elems[e] && field.elems[e].type == 'datetime') {
                apiElem[e] = formatDateForApi(elem[e].value);
            } else {
                apiElem[e] = (elem[e].value != 'null') ? elem[e].value : null;
            }
        });

        apiElem[key] = (value !== undefined) ? value : null;

        saveListElem(elem.id, apiElem, () => {
            elem[key].value = dayjs(value);
        });
    }

    const deleteListElem = (elem, index) => {
        triggerSave();
        apiService.deleteListElem(elem.id).then(async(response) => {
            if (response?.ok) {
                if (field.currentValue) {
                    field.currentValue.value.splice(index, 1);
                }
                resolveSave();
                invalidateQueries();
                // await REFRESH_CA_SHEET();
                // await REFRESH_INPUT_SHEET();
            } else {
                rejectSave(response);
            }
        })
    }

    // Fetch relation options per elem key from API
    const [relationOptionsMap, setRelationOptionsMap] = React.useState<Record<string, any[]>>({});
    const [timingOptionsItems, setTimingOptionsItems] = React.useState([]);
    const [optionsLoading, setOptionsLoading] = React.useState(true);

    React.useEffect(() => {
        // Global library snapshot: its ids belong to another database.
        if (field.snapshot) {
            setOptionsLoading(false);
            return;
        }
        setOptionsLoading(true);
        const promises: Promise<void>[] = [];

        // Fetch relation options for each relation elem key
        Object.keys(field.elems).forEach((key) => {
            if (field.elems[key].type === 'relation') {
                const p = apiService.getListRelationOptions(field.id, key).then(async (response) => {
                    if (response) {
                        const data = await response.json();
                        setRelationOptionsMap(prev => ({ ...prev, [key]: data }));
                    }
                });
                promises.push(p);
            }
        });

        // Fetch timing options if any elem has timing_reference type
        const hasTimingRef = Object.values(field.elems).some((elem: any) => elem.type === 'timing_reference');
        if (hasTimingRef) {
            const p = apiService.getTimingOptions(field.id).then(async (response) => {
                if (response) {
                    const data = await response.json();
                    setTimingOptionsItems(data);
                }
            });
            promises.push(p);
        }

        Promise.all(promises).then(() => setOptionsLoading(false));
    }, [field.id]);

    const getFilteredRelationOptions = (key, index) => {
        const allOptions = relationOptionsMap[key];
        if (!Array.isArray(allOptions)) return [];
        const elem = field.elems?.[key];
        const alreadySelected = (elem?.unique && field.currentValue?.value)
            ? field.currentValue.value.map((e, i) => (i !== index && e[key]?.value != null) ? e[key].value.toString() : null)
            : [];
        return allOptions.filter(r => !alreadySelected.includes(r.id?.toString()));
    }

    const getListElemSelectorChoices = (elem, key, index) => {
        const targetField = siblingFields?.[elem.target_list_field];

        if (!targetField) {
            return [];
        }

        const alreadySelected = (elem.unique && field.currentValue?.value) ? field.currentValue.value.map((e, i) => (i !== index) ? e[key].value : null) : [];

        if (!targetField.value?.value) {
            return [];
        }

        return targetField.value.value.filter(e => !alreadySelected.includes(e.id))

    }

    const setListElemSelectorChoices = (listElemSelectorChoices, elem, key) => {
        listElemSelectorChoices.map((e: any) => {
            if (e.id == elem[key].value) {
                elem[key].currentObj = e
            }
        })
        if (elem[key].currentObj === undefined) {
            elem[key].currentObj = 'null';
        }
    }

    const setTimingById = (elem, key) => {
        timingOptionsItems.forEach((e: any) => {
            if (e.id == elem[key].value) {
                elem[key].currentObj = e
            }
        })
        if (elem[key].currentObj === undefined) {
            elem[key].currentObj = 'null';
        }
    }

    const setCurrentRelationObj = (potentialRelations, elem, key) => {
        potentialRelations.forEach(e => {
            if (e.id == elem[key].value) {
                elem[key].currentObj = e
            }
        })
        if (elem[key].currentObj === undefined) {
            elem[key].currentObj = 'null';
        }
    }

    const choicePanelName = (relationTypes) => {
        let name = "";
        let index = 0;
        relationTypes.forEach(e => {
            if (index != 0) {
                name += ' | '
            }
            name += e;
            index++;
        });
        return name;
    }

    const setDatePickerOpen = (item, value) => {
        item.isOpen = value;
        invalidateQueries();
    }

    const onSelectAllClick = () => {
        if (currentlySelectingAll) {
            return;
        }
        currentlySelectingAll = true;

        const firstKey = Object.keys(field.elems)[0];
        const allOptions = relationOptionsMap[firstKey] || [];

        if (allOptions.length) {
            const idList = allOptions.map(e => e.id)

            apiService.fillModelDataValueList(field.id, idList).then(async(response) => {
                if (response) {
                    const resp = await response.json()

                    if (field.currentValue) {
                        resp.map(newElem => {
                            field.currentValue.value.push(newElem);
                        })
                    }

                    currentlySelectingAll = false;

                    invalidateQueries();
                }
            })
        }
    }

    const onDeselectAllClick = () => {
        if (!field.currentValue?.value?.length) return;

        apiService.clearModelDataValueList(field.id).then(async(response) => {
            if (response) {
                field.currentValue.value = [];
                invalidateQueries();
            }
        })
    }

    const showSelectAll = () => {
        // @ts-ignore
        if (Object.values(field.elems).length == 1 && Object.values(field.elems)[0].type == 'relation' && Object.values(field.elems)[0].unique == true) {
            return true;
        } else {
            return false;
        }
    }

    return (
        <div>
            <div className="mt-1 mr-2 overflow-x-auto">
                <div className="border border-slate-200 rounded-lg overflow-hidden w-fit">
                    <table className="w-full border-collapse">
                        <tbody>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                {Object.keys(field.elems).map(key => (
                                    <td key={key} className="min-w-[200px] whitespace-nowrap p-2">
                                        <div className="font-bold">
                                            <div className="flex flex-row items-center gap-1">
                                                {
                                                    field.elems[key].description ?
                                                    <div className="shrink-0">
                                                        <FieldDescriptionPopover field={field.elems[key]}/>
                                                    </div> : null
                                                }
                                                {snakeToFirstUpper(key)}
                                                {
                                                    showSelectAll() ?
                                                    <>
                                                        <button
                                                            className="ml-1 rounded-md h-[30px] w-[30px] hover:bg-slate-200 flex items-center justify-center"
                                                            onClick={() => onSelectAllClick()}
                                                            title="Select All"
                                                        >
                                                            <span className="material-symbols-outlined text-[20px]">playlist_add_check</span>
                                                        </button>
                                                        <button
                                                            className="rounded-md h-[30px] w-[30px] hover:bg-slate-200 flex items-center justify-center"
                                                            onClick={() => onDeselectAllClick()}
                                                            title="Deselect All"
                                                        >
                                                            <span className="material-symbols-outlined text-[20px]">playlist_remove</span>
                                                        </button>
                                                    </> : null
                                                }
                                            </div>
                                        </div>
                                    </td>
                                ))}
                                <td className="w-[50px]"></td>
                            </tr>
                            {
                                (field.currentValue?.value || []).map((e: any, index) => (
                                    <tr key={e.id || index} className="border-b border-slate-200 hover:bg-slate-50">
                                        {
                                            Object.keys(field.elems).map((key) => {
                                                if (key !== 'id') {
                                                    if (field.elems[key].type === 'relation') {
                                                        const potentialRelations = getFilteredRelationOptions(key, index);
                                                        setCurrentRelationObj(potentialRelations, e, key);

                                                        // Ajouter une option vide au début
                                                        const relationOptions = [
                                                            { value: '', label: '— Select —' },
                                                            ...potentialRelations.map((relation: any) => ({
                                                                value: relation.id.toString(),
                                                                // Prefix with the parent company so revenues from
                                                                // different companies (parent_scope_only=false) are
                                                                // disambiguated. company_name is resolved backend-side.
                                                                label: relation.company_name
                                                                    ? `${relation.company_name} | ${relation.name}`
                                                                    : relation.name,
                                                            }))
                                                        ];

                                                        const currentRelationValue = e[key].currentObj !== 'null' && e[key].currentObj
                                                            ? e[key].currentObj.id.toString()
                                                            : '';

                                                        return (
                                                            <td key={key} className="p-0">
                                                                <TailwindSelect
                                                                    value={currentRelationValue}
                                                                    onChange={(newValue) => {
                                                                        if (!newValue) {
                                                                            // Clear selection
                                                                            e[key].currentObj = 'null';
                                                                            updateRelationalListElem(e, key, null, null, field.elems[key].unique);
                                                                        } else {
                                                                            const selectedRelation = potentialRelations.find(r => r.id.toString() === newValue);
                                                                            if (selectedRelation) {
                                                                                e[key].currentObj = selectedRelation;
                                                                                updateRelationalListElem(e, key, selectedRelation.id, selectedRelation.name, field.elems[key].unique);
                                                                            }
                                                                        }
                                                                    }}
                                                                    options={relationOptions}
                                                                    placeholder={snakeToPascal(choicePanelName(field.elems[key].relation_types))}
                                                                    disabled={isReadOnly}
                                                                    className="w-full h-[42px]"
                                                                    autoWidth
                                                                />
                                                            </td>
                                                        )
                                                    }
                                                    else if (field.elems[key].type === 'timing_reference') {
                                                        setTimingById(e, key);

                                                        // Ajouter une option vide au début
                                                        const timingOptions = [
                                                            { value: '', label: '— Select Timing —' },
                                                            ...timingOptionsItems.map((timing: any) => ({
                                                                value: timing.id.toString(),
                                                                label: snakeToPascal(timing.field_name),
                                                            }))
                                                        ];

                                                        const currentTimingValue = e[key].currentObj !== 'null' && e[key].currentObj
                                                            ? e[key].currentObj.id.toString()
                                                            : '';

                                                        return (
                                                            <td key={key} className="p-0">
                                                                <TailwindSelect
                                                                    value={currentTimingValue}
                                                                    onChange={(newValue) => {
                                                                        if (!newValue) {
                                                                            // Clear selection
                                                                            e[key].currentObj = 'null';
                                                                            updateRelationalListElem(e, key, null, null, field.elems[key].unique);
                                                                        } else {
                                                                            const selectedTiming = timingOptionsItems.find((t: any) => t.id.toString() === newValue);
                                                                            if (selectedTiming) {
                                                                                e[key].currentObj = selectedTiming;
                                                                                updateRelationalListElem(e, key, selectedTiming.id, selectedTiming.field_name, field.elems[key].unique);
                                                                            }
                                                                        }
                                                                    }}
                                                                    options={timingOptions}
                                                                    placeholder="Timing"
                                                                    disabled={isReadOnly}
                                                                    className="w-full h-[42px]"
                                                                    autoWidth
                                                                />
                                                            </td>
                                                        )
                                                    }
                                                    else if (field.elems[key].type === 'list_elem_selector') {

                                                        const choicesItems = getListElemSelectorChoices(field.elems[key], key, index);
                                                        setListElemSelectorChoices(choicesItems, e, key)

                                                        // Ajouter une option vide au début
                                                        const selectorOptions = [
                                                            { value: '', label: '— Select —' },
                                                            ...choicesItems.map((choice: any) => ({
                                                                value: choice.id.toString(),
                                                                label: choice[field.elems[key].target_list_column_anchor].value
                                                                    ? snakeToPascal(choice[field.elems[key].target_list_column_anchor].value)
                                                                    : " ",
                                                            }))
                                                        ];

                                                        const currentSelectorValue = e[key].currentObj !== 'null' && e[key].currentObj
                                                            ? e[key].currentObj.id.toString()
                                                            : '';

                                                        return (
                                                            <td key={key} className="p-0">
                                                                <TailwindSelect
                                                                    value={currentSelectorValue}
                                                                    onChange={(newValue) => {
                                                                        if (!newValue) {
                                                                            // Clear selection
                                                                            e[key].currentObj = 'null';
                                                                            updateRelationalListElem(e, key, null, null, field.elems[key].unique);
                                                                        } else {
                                                                            const selectedChoice = choicesItems.find(c => c.id.toString() === newValue);
                                                                            if (selectedChoice) {
                                                                                e[key].currentObj = selectedChoice;
                                                                                updateRelationalListElem(e, key, selectedChoice.id, selectedChoice.name.value, field.elems[key].unique);
                                                                            }
                                                                        }
                                                                    }}
                                                                    options={selectorOptions}
                                                                    placeholder={snakeToPascal(field.elems[key].target_list_field)}
                                                                    disabled={isReadOnly}
                                                                    className="w-full h-[42px]"
                                                                    autoWidth
                                                                />
                                                            </td>
                                                        )
                                                    }
                                                    else if (field.elems[key].type === 'datetime') {
                                                        // Convert dayjs to Date for TailwindDatePicker
                                                        const dateValue = e[key].value && dayjs.isDayjs(e[key].value)
                                                            ? e[key].value.toDate()
                                                            : e[key].value instanceof Date
                                                                ? e[key].value
                                                                : null;

                                                        return (
                                                            <td key={key} className="p-0">
                                                                <TailwindDatePicker
                                                                    value={dateValue}
                                                                    onChange={(newDate) => {
                                                                        if (newDate) {
                                                                            const dayjsValue = dayjs(newDate);
                                                                            updateListDateElem(e, key, dayjsValue.format('YYYY-MM-DD'));
                                                                        }
                                                                    }}
                                                                    disabled={isReadOnly}
                                                                    className="w-full h-[42px]"
                                                                />
                                                            </td>
                                                        )
                                                    }
                                                    else if (field.elems[key].type === 'enum') {
                                                        e[key].currentObj = (e[key].value) ? e[key].value : 'null';

                                                        const enumOptions = field.elems[key].choices.map(choice => ({
                                                            value: choice,
                                                            label: snakeToPascal(choice),
                                                        }));

                                                        return (
                                                            <td key={key} className="p-0">
                                                                <TailwindSelect
                                                                    value={e[key].currentObj !== 'null' ? e[key].currentObj : ''}
                                                                    onChange={(newValue) => {
                                                                        e[key].currentObj = newValue || 'null';
                                                                        updateListEnumElem(e, key, newValue || 'null');
                                                                    }}
                                                                    options={enumOptions}
                                                                    placeholder={snakeToPascal(key)}
                                                                    disabled={isReadOnly}
                                                                    className="w-full h-[42px]"
                                                                    autoWidth
                                                                />
                                                            </td>
                                                        )
                                                    }
                                                    else {
                                                        return (
                                                            <td key={key} className="p-0">
                                                                <TailwindInput
                                                                    type={field.elems[key].type === 'float' ? 'number' : 'text'}
                                                                    step={field.elems[key].type === 'float' ? 'any' : undefined}
                                                                    value={e[key].value || ''}
                                                                    readOnly={isReadOnly}
                                                                    onChange={(ev) => {
                                                                        updateListElemWithValidation(e, key, field.elems[key].type, ev.target.value);
                                                                    }}
                                                                    onBlur={() => {validateListElem(e);}}
                                                                    placeholder={(field.elems[key].placeholder) ? field.elems[key].placeholder : ''}
                                                                    prefix={field.elems[key].prefix}
                                                                    suffix={field.elems[key].suffix}
                                                                    error={e[key].error}
                                                                    className="w-full h-[42px]"
                                                                />
                                                            </td>
                                                        )
                                                    }
                                                }
                                                else {
                                                    return null;
                                                }
                                            })
                                        }
                                        {
                                            !isReadOnly ?
                                            <td className="p-0 align-middle text-center bg-slate-50">
                                                <button
                                                    className="p-1 hover:bg-slate-200 rounded"
                                                    onClick={() => {
                                                        deleteListElem(e, index);
                                                    }}
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">close</span>
                                                </button>
                                            </td> : <td></td>
                                        }
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="mt-1">
                {
                    !isReadOnly && (!field.max_length || field.value.value.length < field.max_length) ?
                    <button
                        id="add-elem-button"
                        className="ml-1 p-1 hover:bg-slate-100 rounded"
                        onClick={() => {
                            addNewElem();
                        }}
                    >
                        <span className="material-symbols-outlined text-[20px]">add</span>
                    </button> : <div></div>
                }
                <SaveIndicator status={saveStatus} className="ml-2 inline-flex" />
            </div>
            <FieldError field={field} />
        </div>
    )
}
